import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getPlanConfig } from '../config/pricingConfig';
import { backendConfig } from '../config/backendConfig';
import { authFetch } from '../utils/authFetch';
import {
  fetchPremiumAddonQuote,
  createPremiumAddonRazorpayOrder,
  verifyPremiumAddonRazorpay,
  checkoutPremiumAddon,
  fetchPublicMembershipConfig,
  redeemArtmoCodes,
} from '../services/studioOnboardingApi';
import { loadRazorpayScript } from '../utils/loadRazorpay';
import { membershipAllowsPremiumAddonQuote } from '../utils/premiumAddonEligibility';

/** Cold Cloud Functions + getIdToken + TLS; keep generous — modal retries instead of one brittle call. */
const MEMBERSHIP_API_TIMEOUT_MS = 38000;
const MEMBERSHIP_FETCH_ATTEMPTS = 3;
/** Hard cap so the modal never stays on skeleton if the chain stalls (getIdToken + CF + retries). */
const MODAL_MEMBERSHIP_OVERALL_MS = 42000;

async function fetchMembershipJsonWithRetries(uid, apiBase) {
  const url = `${apiBase}/membership/status?uid=${encodeURIComponent(uid)}`;
  let lastErr;
  for (let attempt = 0; attempt < MEMBERSHIP_FETCH_ATTEMPTS; attempt++) {
    try {
      if (attempt > 0) {
        await new Promise((r) => setTimeout(r, 500 * attempt));
      }
      const res = await authFetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: AbortSignal.timeout(MEMBERSHIP_API_TIMEOUT_MS),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr;
}

function withModalOverallTimeout(promise, ms) {
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      const err = new Error('Membership check timed out');
      err.name = 'TimeoutError';
      reject(err);
    }, ms);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => {
    clearTimeout(timeoutId);
  });
}

function friendlyFetchErrorMessage(e, fallback = 'Failed to load') {
  if (e == null) return fallback;
  if (e.name === 'AbortError' || e.name === 'TimeoutError') {
    return 'Request timed out. Check your connection and try again.';
  }
  const m = typeof e.message === 'string' ? e.message : String(e);
  if (/abort/i.test(m) || /timed out/i.test(m)) {
    return 'Request timed out or was interrupted. Please try again.';
  }
  return m || fallback;
}

const MEMBERSHIP_POST_TIMEOUT_MS = 30000;

function abortSignalForPost() {
  return typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function'
    ? AbortSignal.timeout(MEMBERSHIP_POST_TIMEOUT_MS)
    : undefined;
}

/**
 * POST JSON to `/api/membership/...` with timeout and stable error messages (avoids silent failures when body is non-JSON).
 */
async function postMembershipAction(apiBase, path, body = {}) {
  const clean = path.replace(/^\//, '');
  const url = `${apiBase}/membership/${clean}`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal: abortSignalForPost(),
  });
  const text = await res.text();
  let data = {};
  if (text) {
    try {
      data = JSON.parse(text);
    } catch (_) {
      data = { error: text.slice(0, 240) || `HTTP ${res.status}` };
    }
  }
  if (!res.ok) {
    const msg =
      (typeof data.error === 'string' && data.error) ||
      (typeof data.message === 'string' && data.message) ||
      `Request failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    err.payload = data;
    throw err;
  }
  return data;
}

function addonDowngradeScheduled(pa) {
  if (!pa || typeof pa !== 'object' || !pa.applied) return false;
  const d = pa.downgradeOnRenewal;
  return d === true || d === 1 || d === 'true';
}

/** After cancel-downgrade POST succeeds, ensure UI does not keep stale Firestore flags. */
function stripScheduledDowngradeFromPayload(payload) {
  if (!payload?.membership?.premiumAddon) return payload;
  const m = { ...payload.membership };
  const pa = { ...m.premiumAddon };
  delete pa.downgradeOnRenewal;
  delete pa.downgradeRequestedAt;
  return { ...payload, membership: { ...m, premiumAddon: pa } };
}

async function fetchMembershipStatusForModal(apiBase, uid) {
  const url = `${apiBase}/membership/status?uid=${encodeURIComponent(uid)}&_t=${Date.now()}`;
  const res = await authFetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
    cache: 'no-store',
    signal: AbortSignal.timeout(MEMBERSHIP_API_TIMEOUT_MS),
  });
  if (!res.ok) return { ok: false, status: res.status, data: null };
  const data = await res.json();
  return { ok: true, data };
}

const isMembershipModalDevLog =
  typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development';

/** API / Auth may omit `membership` or send `{}` — treat as missing so we never show an endless skeleton. */
function hasDisplayableMembershipPayload(payload) {
  if (!payload?.membership || typeof payload.membership !== 'object') return false;
  return Object.keys(payload.membership).length > 0;
}

/**
 * Temporary payload while /membership/status is loading or returns `{}`.
 * Must match account type: students/parents see base-plan shape; only mentor accounts use mentor tiers.
 */
function createMembershipPlaceholderPayload(user) {
  const accountType = (user?.accountType || 'student').toString();
  const isMentor = accountType === 'mentor';
  const end = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

  if (isMentor) {
    return {
      ok: true,
      allowed: true,
      membership: {
        status: 'active',
        tier: 'mentor-basic',
        autoRenew: true,
        activatedAt: new Date(),
        currentPeriodEnd: end,
        isActive: true,
        isExpired: false,
      },
      expiresAt: end,
    };
  }

  return {
    ok: true,
    allowed: true,
    isMentor: false,
    membership: {
      status: 'active',
      tier: 'base',
      planTier: 'base',
      billingInterval: 'monthly',
      autoRenew: true,
      activatedAt: new Date(),
      currentPeriodEnd: end,
      isActive: true,
      isExpired: false,
    },
    expiresAt: end,
  };
}

/**
 * Legacy identifier (name is historical). Delegates to {@link createMembershipPlaceholderPayload}.
 * Student vs mentor shape is decided inside that function — not mentor-only.
 */
function createMentorFallbackMembershipPayload(user) {
  return createMembershipPlaceholderPayload(user);
}

/** Shown only when we have no cached membership — layout matches the real grid (Stripe / Netflix pattern). */
function SubscriptionModalSkeleton() {
  return (
    <div
      className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-pulse"
      aria-busy="true"
      aria-label="Loading subscription layout"
    >
      <div className="space-y-4">
        <div className="h-7 bg-gray-200 rounded-lg w-40" />
        <div className="h-36 bg-gray-100 rounded-2xl" />
        <div className="space-y-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-4 bg-gray-100 rounded w-full" />
          ))}
        </div>
      </div>
      <div className="space-y-4">
        <div className="h-7 bg-gray-200 rounded-lg w-48" />
        <div className="h-44 bg-gray-100 rounded-2xl" />
        <div className="h-10 bg-gray-100 rounded-xl w-32" />
      </div>
    </div>
  );
}

export default function ManageSubscriptionModal({ isOpen, onClose }) {
  const { user, refreshMembership, membership: ctxMembership } = useAuth();
  const ctxMembershipRef = useRef(ctxMembership);
  ctxMembershipRef.current = ctxMembership;
  const navigate = useNavigate();
  const baseUrl = backendConfig.baseUrl || '';
  const apiBase = baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`;
  /** Background refresh of /membership/status — never blocks the main layout (modern “stale-while-revalidate”). */
  const [msRefreshing, setMsRefreshing] = useState(false);
  const [msLoading, setMsLoading] = useState(false);
  const [msError, setMsError] = useState('');
  const [msStaleNotice, setMsStaleNotice] = useState('');
  const [loadKey, setLoadKey] = useState(0);
  const [msData, setMsData] = useState(null);
  const [showPlanPicker, setShowPlanPicker] = useState(false);
  const [planConfirm, setPlanConfirm] = useState(null);
  const [msSuccess, setMsSuccess] = useState('');
  const [showCancelWarning, setShowCancelWarning] = useState(false);
  const [paQuote, setPaQuote] = useState(null);
  const [paQuoteLoading, setPaQuoteLoading] = useState(false);
  const [paBusy, setPaBusy] = useState(false);
  const [paErr, setPaErr] = useState('');
  const [pubCfg, setPubCfg] = useState(null);
  const [artmoRows, setArtmoRows] = useState(['']);
  const [artmoRedeeming, setArtmoRedeeming] = useState(false);
  const [artmoRedeemError, setArtmoRedeemError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    fetchPublicMembershipConfig()
      .then((c) => {
        if (c && c.ok !== false) setPubCfg(c);
      })
      .catch(() => {});
  }, [isOpen]);

  // Always paint *something* before network: ctx snapshot, else last modal state, else placeholder.
  // Backend can return `membership: {}` (e.g. mentor with missing parent) — that is not "displayable", so without
  // this the grid stays on skeleton until /membership/status finishes (or forever if the request stalls).
  useLayoutEffect(() => {
    if (!isOpen || !user) return;
    const snap = ctxMembershipRef.current;
    setMsData((prev) => {
      if (hasDisplayableMembershipPayload(snap)) return snap;
      if (hasDisplayableMembershipPayload(prev)) return prev;
      return createMentorFallbackMembershipPayload(user);
    });
  }, [isOpen, user, ctxMembership]);

  const applyCheckoutGraceToData = useCallback((data) => {
    if (!data?.membership) return data;
    try {
      const activatedAt = parseInt(localStorage.getItem('membershipJustActivatedAt') || '0', 10);
      const activatedPlan = localStorage.getItem('membershipJustActivatedPlan') || '';
      const fresh = activatedAt && Date.now() - activatedAt < 10 * 60 * 1000;
      if (fresh && activatedPlan) {
        return {
          ...data,
          membership: {
            ...(data.membership || {}),
            tier: activatedPlan,
            status: 'active',
          },
        };
      }
    } catch (_) {}
    if (data.membership && data.membership.autoRenew === undefined) {
      return { ...data, membership: { ...data.membership, autoRenew: true } };
    }
    return data;
  }, []);

  useEffect(() => {
    if (!isOpen || !user) return;
    let cancelled = false;
    setMsRefreshing(true);
    setMsError('');
    setMsStaleNotice('');

    (async () => {
      try {
        const raw = await withModalOverallTimeout(
          fetchMembershipJsonWithRetries(user.uid, apiBase),
          MODAL_MEMBERSHIP_OVERALL_MS
        );
        if (cancelled) return;
        let data = raw;
        if (!hasDisplayableMembershipPayload(data)) {
          data = createMentorFallbackMembershipPayload(user);
        } else {
          data = applyCheckoutGraceToData(data);
        }
        setMsData(data);
        setMsError('');
        setMsStaleNotice('');
        void refreshMembership(user.uid, { force: true }).catch(() => {});
      } catch (e) {
        if (cancelled) return;
        const cached = ctxMembershipRef.current;
        if (hasDisplayableMembershipPayload(cached)) {
          setMsData(cached);
          setMsError('');
          setMsStaleNotice(
            'We could not refresh your subscription from the server. Showing your last loaded details — try again in a moment.'
          );
        } else {
          setMsError(friendlyFetchErrorMessage(e, 'Failed to load'));
          setMsData(createMentorFallbackMembershipPayload(user));
        }
      } finally {
        if (!cancelled) setMsRefreshing(false);
      }
    })();

    return () => {
      cancelled = true;
      setMsRefreshing(false);
    };
  }, [isOpen, user, apiBase, applyCheckoutGraceToData, refreshMembership, loadKey]);

  /** If the modal closes mid-fetch, reset so chips / spinners never stick. */
  useEffect(() => {
    if (!isOpen) {
      setMsLoading(false);
      setMsRefreshing(false);
      setPaBusy(false);
      setPaQuoteLoading(false);
    }
  }, [isOpen]);

  const PREMIUM_QUOTE_TIMEOUT_MS = 20000;

  useEffect(() => {
    if (!isOpen || !user) {
      setPaQuoteLoading(false);
      return;
    }
    const payload = msData ?? ctxMembership ?? null;
    if (!membershipAllowsPremiumAddonQuote(payload)) {
      setPaQuote(null);
      setPaQuoteLoading(false);
      return;
    }
    let cancelled = false;
    setPaQuoteLoading(true);
    setPaErr('');
    const quotePromise = fetchPremiumAddonQuote();
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('quote_timeout')), PREMIUM_QUOTE_TIMEOUT_MS);
    });
    Promise.race([quotePromise, timeoutPromise])
      .then((q) => {
        if (!cancelled && q?.ok !== false) setPaQuote(q);
        else if (!cancelled) setPaQuote(null);
      })
      .catch((e) => {
        if (!cancelled) {
          setPaQuote(null);
          const msg =
            e?.message === 'quote_timeout'
              ? 'Price preview timed out. You can still open checkout — the amount is confirmed in Razorpay.'
              : 'Could not load price preview. You can still proceed to checkout.';
          setPaErr(msg);
        }
      })
      .finally(() => {
        if (!cancelled) setPaQuoteLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, user, msData, ctxMembership]);

  useEffect(() => {
    if (!isOpen) {
      setArtmoRows(['']);
      setArtmoRedeemError('');
      setMsStaleNotice('');
    }
  }, [isOpen]);

  const runRedeemArtmoInModal = useCallback(async () => {
    if (!user?.uid) return;
    const codes = artmoRows.map((c) => String(c || '').trim()).filter(Boolean);
    if (!codes.length) {
      setArtmoRedeemError('Enter at least one Artmo code.');
      return;
    }
    setArtmoRedeeming(true);
    setArtmoRedeemError('');
    setMsError('');
    try {
      const data = await redeemArtmoCodes(codes);
      const applied =
        Array.isArray(data.validCodes) && data.validCodes.length
          ? data.validCodes.length
          : (data.monthsGranted ?? 0);
      if (!applied) {
        setArtmoRedeemError('None of the codes could be applied. Check each code and try again.');
        return;
      }
      try {
        await Promise.race([
          refreshMembership(user.uid, { force: true }),
          new Promise((_, reject) => setTimeout(() => reject(new Error('refresh_timeout')), 12000)),
        ]);
      } catch (_) {
        /* non-blocking */
      }
      const res = await authFetch(`${apiBase}/membership/status?uid=${user.uid}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: AbortSignal.timeout(MEMBERSHIP_API_TIMEOUT_MS),
      });
      let sj = null;
      if (res.ok) {
        sj = await res.json();
        setMsData(sj);
      }
      const months = data.monthsGranted ?? applied;
      let endLabel = '';
      try {
        const pe = data.periodEnd || sj?.expiresAt || sj?.membership?.currentPeriodEnd;
        if (pe) {
          const d = typeof pe?.toDate === 'function' ? pe.toDate() : new Date(pe);
          if (!Number.isNaN(d.getTime())) {
            endLabel = ` New access through ${d.toLocaleDateString()}.`;
          }
        }
      } catch (_) {
        /* ignore */
      }
      setMsSuccess(`Applied ${months} month(s) from your Artmo code(s).${endLabel}`);
      setArtmoRows(['']);
      window.dispatchEvent(new CustomEvent('subscriptionUpdated'));
    } catch (e) {
      setArtmoRedeemError(friendlyFetchErrorMessage(e, 'Could not apply codes'));
    } finally {
      setArtmoRedeeming(false);
    }
  }, [user, artmoRows, refreshMembership, apiBase]);

  const runPremiumAddonUpgrade = useCallback(async () => {
    if (!user?.uid) return;
    setPaBusy(true);
    setPaErr('');
    try {
      let cfg = pubCfg;
      if (!cfg) {
        cfg = await fetchPublicMembershipConfig();
        if (cfg && cfg.ok !== false) setPubCfg(cfg);
      }
      const useSimulate =
        cfg && cfg.premiumSimulateAllowed !== false && !cfg.razorpayConfigured;
      if (useSimulate) {
        await checkoutPremiumAddon();
        void refreshMembership(user.uid, { force: true }).catch(() => {});
        const res = await authFetch(`${apiBase}/membership/status?uid=${user.uid}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          signal: AbortSignal.timeout(MEMBERSHIP_API_TIMEOUT_MS),
        });
        if (res.ok) {
          const sj = await res.json();
          setMsData(sj);
        }
        setMsSuccess('Unlimited add-on applied for your current period.');
        window.dispatchEvent(new CustomEvent('subscriptionUpdated'));
        setPaBusy(false);
        return;
      }
      await loadRazorpayScript();
      if (typeof window.Razorpay !== 'function') {
        throw new Error('Payment could not be loaded. Please refresh and try again.');
      }
      const order = await createPremiumAddonRazorpayOrder();
      const descParts = [];
      if (order.billableMonths != null) {
        const d = order.prorationMonthDays ?? 30;
        descParts.push(`Prorated ~${Number(order.billableMonths).toFixed(2)} × ${d}-day month(s)`);
      }
      if (order.periodEnd) {
        try {
          descParts.push(`through ${new Date(order.periodEnd).toLocaleDateString()}`);
        } catch (_) {
          descParts.push('for your current access period');
        }
      }
      const description =
        descParts.length > 0
          ? `Unlimited add-on (${descParts.join(', ')})`
          : 'Studio unlimited add-on (prorated)';
      const opts = {
        key: order.keyId,
        amount: order.amount,
        currency: order.currency || 'INR',
        order_id: order.orderId,
        name: 'Artmosphere Studio',
        description,
        handler: async (response) => {
          try {
            setPaBusy(true);
            setPaErr('');
            await verifyPremiumAddonRazorpay({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            void refreshMembership(user.uid, { force: true }).catch(() => {});
            const res = await authFetch(`${apiBase}/membership/status?uid=${user.uid}`, {
              method: 'GET',
              headers: { 'Content-Type': 'application/json' },
              signal: AbortSignal.timeout(MEMBERSHIP_API_TIMEOUT_MS),
            });
            if (res.ok) {
              const sj = await res.json();
              setMsData(sj);
            }
            setMsSuccess('Unlimited add-on applied for your current period.');
            window.dispatchEvent(new CustomEvent('subscriptionUpdated'));
          } catch (err) {
            setPaErr(err.message || 'Payment verification failed');
          } finally {
            setPaBusy(false);
          }
        },
        prefill: {
          email: user?.email || '',
          name: user?.displayName || '',
        },
        theme: { color: '#d97706' },
        modal: {
          ondismiss: () => setPaBusy(false),
        },
      };
      const rzp = new window.Razorpay(opts);
      rzp.on('payment.failed', (resp) => {
        setPaErr(resp?.error?.description || 'Payment failed.');
        setPaBusy(false);
      });
      setPaBusy(false);
      rzp.open();
    } catch (e) {
      setPaErr(e.message || 'Could not start payment');
      setPaBusy(false);
    }
  }, [user, pubCfg, refreshMembership, apiBase]);

  /** Used by paid checkout button label when Razorpay modal is about to open. */
  const paidCheckoutButtonLabel = (busy, cfg) => {
    if (busy) return 'Opening checkout…';
    if (!cfg) return 'Continue to checkout';
    const rz = cfg.razorpayConfigured === true;
    const sim = cfg.premiumSimulateAllowed !== false && !cfg.razorpayConfigured;
    if (rz) return 'Pay with card / UPI — open Razorpay checkout';
    if (sim) return 'Simulate payment & apply add-on (no card in dev)';
    return 'Continue to checkout';
  };


  const runDowngradeAddonOnRenewal = useCallback(async () => {
    if (!user?.uid) return;
    setPaBusy(true);
    setPaErr('');
    try {
      await postMembershipAction(apiBase, 'premium-addon/downgrade-next-renewal');
      void refreshMembership(user.uid, { force: true }).catch(() => {});
      const { ok, data } = await fetchMembershipStatusForModal(apiBase, user.uid);
      if (ok && data) setMsData(data);
      setMsSuccess(
        'Downgrade scheduled: the unlimited add-on will drop off after your next billing cycle (you keep Pro limits until then).'
      );
      window.dispatchEvent(new CustomEvent('subscriptionUpdated'));
    } catch (e) {
      setPaErr(friendlyFetchErrorMessage(e, 'Could not schedule downgrade'));
    } finally {
      setPaBusy(false);
    }
  }, [user, apiBase, refreshMembership]);

  const runCancelDowngradeRenewal = useCallback(async () => {
    if (!user?.uid) return;
    setPaBusy(true);
    setPaErr('');
    try {
      await postMembershipAction(apiBase, 'premium-addon/cancel-downgrade-renewal');
      if (isMembershipModalDevLog) {
        // eslint-disable-next-line no-console
        console.log('[ManageSubscription] cancel-downgrade-renewal POST ok');
      }
      void refreshMembership(user.uid, { force: true }).catch(() => {});

      setMsData((prev) => {
        const base = prev ?? ctxMembershipRef.current;
        if (!base?.membership?.premiumAddon?.applied) return prev ?? base;
        return stripScheduledDowngradeFromPayload(base);
      });

      let { ok, data: sj } = await fetchMembershipStatusForModal(apiBase, user.uid);
      if (!ok) {
        if (isMembershipModalDevLog) {
          // eslint-disable-next-line no-console
          console.warn('[ManageSubscription] membership/status failed after cancel; keeping optimistic strip');
        }
      } else if (sj && addonDowngradeScheduled(sj.membership?.premiumAddon)) {
        if (isMembershipModalDevLog) {
          // eslint-disable-next-line no-console
          console.warn('[ManageSubscription] status still had downgradeOnRenewal; retrying once');
        }
        await new Promise((r) => setTimeout(r, 450));
        const second = await fetchMembershipStatusForModal(apiBase, user.uid);
        if (second.ok && second.data) sj = second.data;
        if (sj && addonDowngradeScheduled(sj.membership?.premiumAddon)) {
          if (isMembershipModalDevLog) {
            // eslint-disable-next-line no-console
            console.warn('[ManageSubscription] applying client strip — POST already succeeded');
          }
          sj = stripScheduledDowngradeFromPayload(sj);
        }
      }
      if (sj) setMsData(sj);

      setMsSuccess(
        'Add-on restored for future renewals — no charge (you only cancelled the scheduled downgrade).'
      );
      window.dispatchEvent(new CustomEvent('subscriptionUpdated'));
    } catch (e) {
      const msg = friendlyFetchErrorMessage(e, 'Could not update add-on');
      if (isMembershipModalDevLog) {
        // eslint-disable-next-line no-console
        console.error('[ManageSubscription] cancel-downgrade error', e);
      }
      if (e?.status === 400 && String(e.message || '').includes('no_scheduled_downgrade')) {
        try {
          void refreshMembership(user.uid, { force: true }).catch(() => {});
          const s2 = await fetchMembershipStatusForModal(apiBase, user.uid);
          if (s2.ok && s2.data) setMsData(s2.data);
        } catch (_) {
          /* ignore */
        }
        setPaErr(
          'Nothing to undo — subscription was already refreshed. If this keeps happening, close the modal and open it again.'
        );
        return;
      }
      setPaErr(msg);
    } finally {
      setPaBusy(false);
    }
  }, [user, apiBase, refreshMembership]);

  /** Stale-while-revalidate: prefer fresh modal state, fall back to AuthContext so the UI never waits on white space. */
  const subscriptionPayload = msData ?? ctxMembership ?? null;
  const membership = subscriptionPayload?.membership || {};
  // Treat any `status: 'trialing'` membership as a free 7‑day pilot (no auto‑renew, show trial copy)
  const isFreeTrial = membership.status === 'trialing';
  const isArtmoCodeMembership =
    membership.tier === 'artmocode' ||
    membership.planTier === 'artmocode' ||
    membership.accessSource === 'artmocode';
  const premiumAddon = membership.premiumAddon;
  /** Match ArtmoRedeemSuccessPage + GET /membership/status `hasPremiumAddon` (user doc can flag add-on if nested object lags). */
  /** Prefer nested `applied`; root `hasPremiumAddon` can lag after server strips the add-on. */
  const hasPremiumAddon = !!(
    premiumAddon?.applied === true ||
    (subscriptionPayload?.hasPremiumAddon === true && premiumAddon?.applied !== false)
  );
  /** User asked to drop add-on next renewal — show upgrade card again to restore. */
  const scheduledDowngrade = addonDowngradeScheduled(premiumAddon);
  /** When off, “next renewal” copy is misleading — period end / Next Payment is the relevant date. */
  const autoRenewOff = membership.autoRenew === false;
  const planTier = membership.planTier || (membership.tier === 'pro' ? 'pro' : 'base');
  const billingInterval = membership.billingInterval || 'monthly';
  const currentPlanConfig = getPlanConfig({ planTier, billingInterval });
  const accountType = (user?.accountType || 'student').toString();
  const showArtmoExtendCard = !!subscriptionPayload && !subscriptionPayload.isMentor && accountType === 'student';
  const hasSubscriptionToShow = hasDisplayableMembershipPayload(subscriptionPayload);
  const membershipAllowsAddonQuote = membershipAllowsPremiumAddonQuote(subscriptionPayload);
  const upgradeEnabled = pubCfg?.isUpgradeEnabled !== false;
  /** Paid prorated checkout — only when user does not already have the add-on and quote is allowed. */
  const showPaidPremiumCheckout = !hasPremiumAddon && upgradeEnabled && membershipAllowsAddonQuote;
  const razorpayReady = pubCfg?.razorpayConfigured === true;
  /** Dev: no Razorpay keys → backend uses simulated checkout (no card UI). */
  const isSimulatedCheckoutOnly =
    !!(pubCfg && pubCfg.premiumSimulateAllowed !== false && !pubCfg.razorpayConfigured);
  /** Show Premium add-on panel (how checkout works + CTA when eligible). Hidden only if admin disables upgrades. */
  const showPremiumAddonPanel = upgradeEnabled;

  useEffect(() => {
    if (!isOpen || !user) return;
    if (hasSubscriptionToShow) return;
    if (!msRefreshing) return;
    const id = window.setTimeout(() => {
      setMsStaleNotice((n) =>
        n ||
        'Taking longer than usual to load subscription details. Try again or check your connection.'
      );
    }, 10000);
    return () => window.clearTimeout(id);
  }, [isOpen, user, hasSubscriptionToShow, msRefreshing]);

  if (!isOpen) return null;

  // When a plan is selected, show only the confirmation popup,
  // not the full "Manage Subscription" window behind it.
  if (planConfirm) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4">
        <div className="bg-white w-full max-w-4xl rounded-xl shadow-xl">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#FDF3D6] via-[#FBEEC8] to-[#F8E8BE] p-6 text-amber-950 border-b border-amber-100 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                {(() => {
                  const isSamePlan = planConfirm.current === true;
                  const isTrialToPaid = isFreeTrial && !isSamePlan;

                  if (isTrialToPaid) {
                    return (
                      <>
                        <h3 className="text-2xl font-bold">Upgrade to {planConfirm.label}</h3>
                        <p className="text-amber-900/75 mt-1">
                          Your free trial will end now and your {planConfirm.label.toLowerCase()} will start immediately.
                        </p>
                      </>
                    );
                  }

                  if (!isSamePlan) {
                    return (
                      <>
                        <h3 className="text-2xl font-bold">Switch to {planConfirm.label}</h3>
                        <p className="text-amber-900/75 mt-1">
                          Your new plan will start immediately after confirmation.
                        </p>
                      </>
                    );
                  }

                  return (
                    <>
                      <h3 className="text-2xl font-bold">Current Plan: {planConfirm.label}</h3>
                      <p className="text-amber-900/75 mt-1">You're already on this plan</p>
                    </>
                  );
                })()}
              </div>
              <button 
                onClick={() => setPlanConfirm(null)}
                className="text-white/80 hover:text-white hover:bg-white/20 rounded-full p-2 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Inline error / success for this popup */}
            {msError && (
              <div className="mb-2 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
                {msError}
              </div>
            )}
            {msSuccess && (
              <div className="mb-2 p-3 bg-green-50 border border-green-200 rounded-lg text-xs text-green-700">
                {msSuccess}
              </div>
            )}

            {/* Simple summary cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Current Plan (read-only) */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="text-sm font-bold text-gray-900 mb-2">Current plan</h4>
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-3 py-1 bg-gray-700 text-white rounded-full text-xs font-bold">
                    {(membership.tier || 'base').toUpperCase()}
                  </span>
                  <span className="text-xs text-gray-600">
                    Status:{' '}
                    <span className="font-semibold text-green-600">
                      {membership.status || 'active'}
                    </span>
                  </span>
                </div>
              </div>

              {/* New Plan (selected) */}
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                <h4 className="text-sm font-bold text-amber-900 mb-2">New plan</h4>
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-3 py-1 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-full text-xs font-bold">
                    {planConfirm.label.toUpperCase()}
                  </span>
                  <span className="text-xs text-amber-800">
                    Rate:{' '}
                    <span className="font-semibold">
                      {planConfirm.price === 0 ? 'Free' : `₹${planConfirm.price}/month`}
                    </span>
                  </span>
                </div>
              </div>
            </div>

            {/* Billing + note */}
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200 space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-amber-700">Plan:</span>
                <span className="font-semibold text-amber-900">{planConfirm.label}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-amber-700">Monthly amount:</span>
                <span className="font-semibold text-amber-900">
                  {planConfirm.price === 0 ? 'Free' : `₹${planConfirm.price}`}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-amber-700">Status:</span>
                <span className="font-semibold text-amber-900">
                  {planConfirm.current
                    ? 'No change – already on this plan'
                    : isFreeTrial
                    ? 'Upgrading from free trial'
                    : 'Plan will update immediately'}
                </span>
              </div>
              <div className="mt-2 pt-2 border-t border-amber-200 text-[11px] text-amber-900">
                {planConfirm.current ? (
                  <span>
                    You are already on the <strong>{planConfirm.label}</strong> plan. No changes will be made.
                  </span>
                ) : isFreeTrial ? (
                  <span>
                    Confirming will end your free trial today and start your paid{' '}
                    <strong>{planConfirm.label.toLowerCase()}</strong> plan immediately.
                  </span>
                ) : (
                  <span>
                    Your new plan will start right away. Future renewals will use this plan and price.
                  </span>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button 
                onClick={() => setPlanConfirm(null)} 
                className="flex-1 px-4 py-3 border-2 border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50 transition-all duration-200 font-bold"
              >
                Cancel
              </button>
              <button
                disabled={msLoading}
                onClick={async () => {
                  if (!user) return;
                  setMsLoading(true);
                  setMsError('');
                  setMsSuccess('');
                  try {
                    const isTrialingMembership = membership.status === 'trialing';

                    if (isTrialingMembership) {
                      // For free trial accounts, jump straight into the checkout modal on the membership page
                      navigate('/membership', {
                        state: {
                          fromManageModal: true,
                          selectedPlan: planConfirm.id === 'pro' ? 'pro' : 'basic',
                          autoCheckout: true,
                        },
                      });
                      return;
                    }

                    const res = await authFetch(
                      `${apiBase}/membership/upgrade-plan`,
                      {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          uid: user.uid,
                          newPlanType: planConfirm.id,
                          seatCount: 1,
                        }),
                      }
                    );

                    const data = await res.json();
                    if (!res.ok) throw new Error(data.error || 'Failed to change plan');

                    if (data.billing) {
                      setMsData(prev => ({
                        ...prev,
                        membership: {
                          ...(prev?.membership || {}),
                          tier: planConfirm.id,
                          status: 'active',
                          lastUpdated: new Date(),
                        },
                      }));
                    }

                    setShowPlanPicker(false);
                    setPlanConfirm(null);

                    setMsSuccess(`Your plan has been updated to ${planConfirm.label}.`);
                  } catch (e) {
                    setMsError(e.message || 'Failed to change plan');
                  } finally {
                    setMsLoading(false);
                  }
                }}
                className="flex-1 px-4 py-3 bg-amber-200 text-amber-950 border border-amber-300 rounded-lg hover:bg-amber-300 transition-all duration-200 font-bold disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {msLoading ? 'Working…' : 'Confirm Plan'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
        <div className="fixed inset-0 bg-amber-900/20 backdrop-blur-sm" style={{ 
          display: 'grid', 
          placeItems: 'center', 
          padding: '2rem',
          minHeight: '100vh',
          overflow: 'auto',
          zIndex: 9999,
          top: 0,
          left: 0,
          right: 0,
          bottom: 0
        }}>
          <div className="bg-white w-full max-w-4xl max-h-[85vh] rounded-3xl shadow-2xl border border-amber-200/80 overflow-hidden transform transition-all duration-300 scale-100 flex flex-col relative" style={{ margin: 'auto' }}>
            {/* Header */}
            <div className="bg-gradient-to-r from-[#FDF3D6] via-[#FBEEC8] to-[#F8E8BE] p-6 text-amber-950 border-b border-amber-100 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-2xl font-bold">Manage Your Subscription</h2>
                    {msRefreshing && hasSubscriptionToShow && (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-900 bg-white/75 rounded-full px-2.5 py-1 border border-amber-100">
                        <span
                          className="h-3.5 w-3.5 border-2 border-amber-950/30 border-t-amber-950 rounded-full animate-spin"
                          aria-hidden
                        />
                        Updating
                      </span>
                    )}
                  </div>
                  <p className="text-amber-900/75 mt-1">Manage plan, billing, and add-ons.</p>
                </div>
                <button 
                  onClick={() => onClose()}
                  className="text-amber-900/70 hover:text-amber-950 hover:bg-white/75 rounded-full p-2 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-8 flex-1 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 200px)' }}>
              {msError && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-red-700 font-medium">{msError}</span>
                  </div>
                </div>
              )}

              {msSuccess && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-green-700 font-medium">{msSuccess}</span>
                  </div>
                </div>
              )}

              {msStaleNotice && (
                <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex flex-wrap items-center justify-between gap-3">
                  <span className="text-amber-900 text-sm">{msStaleNotice}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setMsStaleNotice('');
                      setLoadKey((k) => k + 1);
                    }}
                    className="shrink-0 px-3 py-1.5 rounded-lg bg-amber-200 text-amber-950 border border-amber-300 text-sm font-semibold hover:bg-amber-300"
                  >
                    Try again
                  </button>
                </div>
              )}

              {hasSubscriptionToShow && !planConfirm && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column - Current Plan Details */}
                <div className="space-y-6">
                  <div className="bg-amber-50/50 border border-amber-100 rounded-2xl p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Current Plan</h3>
                    
                    {/* Plan Badge */}
                    <div className="flex items-center gap-3 mb-4">
                      <span className="px-4 py-2 bg-amber-400 text-amber-950 rounded-full text-sm font-bold">
                        {hasPremiumAddon
                          ? 'ARTMOCODE + PREMIUM'
                          : isArtmoCodeMembership
                            ? 'ARTMOCODE (BASE)'
                            : membership?.tier
                              ? String(membership.tier).toUpperCase()
                              : 'NO PLAN'}
                      </span>
                      <span className="text-gray-600 text-sm">
                        {membership?.status === 'active' ? 'Active' : 
                         membership?.status === 'trialing' ? 'Trial' : 
                         membership?.status === 'expired' ? 'Expired' : 'Inactive'}
                      </span>
                    </div>

                  </div>

                  {/* Membership Details */}
                  <div className="bg-white border border-gray-200 rounded-2xl p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Membership Details</h3>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Member Since:</span>
                        <span className="font-medium">
                          {(() => {
                            const d = membership.activatedAt || membership.trialStartsAt;
                            const dt = d ? (d._seconds ? new Date(d._seconds * 1000) : new Date(d)) : null;
                            if (!dt) return '—';
                            const dd = String(dt.getDate()).padStart(2, '0');
                            const mm = String(dt.getMonth() + 1).padStart(2, '0');
                            const yyyy = dt.getFullYear();
                            return `${dd}/${mm}/${yyyy}`;
                          })()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Status:</span>
                        <span className={`font-medium ${membership.status === 'active' ? 'text-green-600' : 'text-red-600'}`}>
                          {membership.status || '—'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Auto Renew:</span>
                        <span className="font-medium">{isFreeTrial ? 'Not applicable (free trial)' : (membership.autoRenew !== false ? 'On' : 'Off')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{isFreeTrial ? 'Trial ends:' : 'Next Payment:'}</span>
                        <span className="font-medium">
                          {(() => {
                            const raw = membership.status === 'trialing'
                              ? membership.trialEndsAt
                              : (membership.currentPeriodEnd || subscriptionPayload?.expiresAt);
                            const dt = raw ? (raw._seconds ? new Date(raw._seconds * 1000) : new Date(raw)) : null;
                            if (!dt) return '—';
                            const dd = String(dt.getDate()).padStart(2, '0');
                            const mm = String(dt.getMonth() + 1).padStart(2, '0');
                            const yyyy = dt.getFullYear();
                            return `${dd}/${mm}/${yyyy}`;
                          })()}
                          {isFreeTrial && ' · Renew before this date to continue access'}
                        </span>
                      </div>
                    </div>
                    {isFreeTrial && (
                      <p className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                        Free 7-day trial; renew before end date to continue access.
                      </p>
                    )}
                  </div>
                </div>

                {/* Right Column - Actions & Pricing */}
                <div className="space-y-6">
                  {/* Pricing */}
                  <div className="bg-gradient-to-br from-amber-50 to-amber-100/60 border border-amber-100 rounded-2xl p-6 text-center">
                    {isFreeTrial ? (
                      <>
                        <div className="text-3xl font-bold text-gray-900 mb-1">Free</div>
                        <div className="text-sm text-gray-700">7‑day pilot access · No auto‑renew</div>
                      </>
                    ) : (
                      <div>
                        <div className="text-xs uppercase tracking-wide text-amber-700 font-semibold mb-1">
                          {hasPremiumAddon
                            ? 'Artmo Code + Premium add-on'
                            : isArtmoCodeMembership
                              ? 'Artmo Code (Base)'
                              : planTier === 'pro'
                                ? 'Pro Plan'
                                : 'Base Plan'}
                        </div>
                        <div className="text-4xl font-bold text-gray-900 mb-1">
                          {isArtmoCodeMembership || hasPremiumAddon
                            ? hasPremiumAddon && premiumAddon?.totalPaid != null
                              ? `₹${Number(premiumAddon.totalPaid).toLocaleString('en-IN')}`
                              : 'Free'
                            : currentPlanConfig
                              ? `₹${currentPlanConfig.amount.toLocaleString('en-IN')}`
                              : '₹199'}
                          <span className="text-lg font-normal text-gray-600">
                            {isArtmoCodeMembership || hasPremiumAddon
                              ? hasPremiumAddon
                                ? ' add-on paid'
                                : ' · code access'
                              : `/${
                                  billingInterval === 'yearly'
                                    ? 'year'
                                    : billingInterval === 'semiannual'
                                      ? '6 months'
                                      : billingInterval === 'quarterly'
                                        ? '3 months'
                                        : 'month'
                                }`}
                          </span>
                        </div>
                        <div className="text-xs text-gray-700">
                          {hasPremiumAddon
                            ? 'Premium limits are active for your current Artmo period.'
                            : isArtmoCodeMembership
                              ? 'Base access from book codes; redeem more codes to extend.'
                              : (currentPlanConfig?.billingLabel || 'Billed every month')}
                          {!isArtmoCodeMembership && currentPlanConfig?.savingsPct > 0 && (
                            <span className="ml-1 text-amber-700 font-semibold">
                              {currentPlanConfig.savingsPct === 10
                                ? '· Save 10%'
                                : '· Best value · Save 20%'}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {showPremiumAddonPanel && showPaidPremiumCheckout && (
                    <div className="rounded-2xl border border-amber-200 bg-amber-50/70 p-4 text-left space-y-3">
                      {paQuoteLoading && (
                        <p className="text-xs text-amber-800 font-medium">Preparing price…</p>
                      )}
                      {!paQuoteLoading && paQuote?.amountRupees != null && (
                        <p className="text-sm font-semibold text-amber-900">
                          Estimated checkout: ₹{Number(paQuote.amountRupees).toLocaleString('en-IN')}
                        </p>
                      )}
                      {paErr && (
                        <p className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-lg px-2 py-1">
                          {paErr}
                        </p>
                      )}
                      <button
                        type="button"
                        disabled={paBusy || paQuoteLoading}
                        onClick={runPremiumAddonUpgrade}
                        className="w-full py-3 rounded-xl font-semibold bg-amber-200 text-amber-950 border border-amber-300 hover:bg-amber-300 disabled:opacity-50 shadow-md"
                      >
                        {paQuoteLoading ? 'Preparing price…' : paidCheckoutButtonLabel(paBusy, pubCfg)}
                      </button>
                    </div>
                  )}

                  {showArtmoExtendCard && (
                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50/90 p-4 text-left space-y-3">
                      <div>
                        <h4 className="text-sm font-bold text-emerald-950">Apply Artmo code(s)</h4>
                        <p className="text-xs text-emerald-900/90 mt-1">
                          Each valid code adds one month to your current access period.
                        </p>
                      </div>
                      <div className="space-y-2">
                        {artmoRows.map((row, i) => (
                          <div key={`artmo-manage-${i}`} className="flex gap-2 items-stretch">
                            <input
                              type="text"
                              value={row}
                              onChange={(e) => {
                                const next = [...artmoRows];
                                next[i] = e.target.value;
                                setArtmoRows(next);
                              }}
                              className="flex-1 min-h-[42px] rounded-xl border border-emerald-200 px-3 bg-white text-emerald-950 placeholder:text-emerald-400/80 text-sm"
                              placeholder={`Code ${i + 1}`}
                              autoCapitalize="characters"
                              autoComplete="off"
                            />
                            {artmoRows.length > 1 && (
                              <button
                                type="button"
                                className="shrink-0 px-2 text-xs text-red-600 hover:underline self-center"
                                onClick={() => setArtmoRows((r) => r.filter((_, j) => j !== i))}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => setArtmoRows((r) => [...r, ''])}
                          className="text-xs font-semibold text-emerald-800 hover:text-emerald-950"
                        >
                          + Add another code
                        </button>
                      </div>
                      {artmoRedeemError && (
                        <p className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-lg px-2 py-1">
                          {artmoRedeemError}
                        </p>
                      )}
                      <button
                        type="button"
                        disabled={artmoRedeeming || msLoading}
                        onClick={runRedeemArtmoInModal}
                        className="w-full py-2.5 rounded-xl font-semibold bg-gradient-to-r from-emerald-600 to-teal-600 text-white disabled:opacity-50"
                      >
                        {artmoRedeeming ? 'Applying…' : 'Apply code(s)'}
                      </button>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="space-y-4">

                    {scheduledDowngrade && (
                      <div className="rounded-2xl border border-violet-200 bg-violet-50/80 p-4 text-left space-y-2">
                        <h4 className="text-sm font-bold text-violet-950">
                          Scheduled downgrade
                        </h4>
                        <p className="text-xs text-violet-900/90 leading-relaxed">
                          Downgrade is scheduled; cancel it below to keep the add-on active on renewals.
                        </p>
                        {paErr && (
                          <p className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-lg px-2 py-1">
                            {paErr}
                          </p>
                        )}
                        <button
                          type="button"
                          disabled={paBusy}
                          onClick={runCancelDowngradeRenewal}
                        className="w-full py-2.5 rounded-xl font-semibold bg-amber-200 text-amber-950 border border-amber-300 hover:bg-amber-300 disabled:opacity-50"
                        >
                          {paBusy ? 'Processing…' : 'Keep unlimited access add-on'}
                        </button>
                      </div>
                    )}

                    {/* Cancel/Resume Button */}
                    <button
                      disabled={msLoading}
                      onClick={async () => {
                        if (!user) return;
                        
                        // If resuming, proceed directly
                        if (membership.canceled) {
                          setMsLoading(true); setMsError("");
                          try {
                            const res = await authFetch(`${apiBase}/membership/portal`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ uid: user.uid, action: 'resume' })
                            });
                            const ct = res.headers.get('content-type') || '';
                            const j = ct.includes('application/json') ? await res.json() : null;
                            if (!res.ok) throw new Error((j && j.error) || 'Failed to update subscription');
                            const s = await authFetch(`${apiBase}/membership/status?uid=${user.uid}`);
                            const sct = s.headers.get('content-type') || '';
                            const sj = sct.includes('application/json') ? await s.json() : null;
                            if (!s.ok) throw new Error((sj && sj.error) || 'Failed to fetch status');
                            setMsData(sj);
                            setMsSuccess('Subscription resumed successfully!');
                            // Close the modal and trigger dashboard refresh
                            setTimeout(() => {
                              onClose();
                              // Dispatch a custom event to notify the organization dashboard
                              window.dispatchEvent(new CustomEvent('subscriptionUpdated'));
                            }, 2000);
                          } catch (e) {
                            setMsError(e.message);
                          } finally {
                            setMsLoading(false);
                          }
                        } else {
                          // If canceling, show warning popup
                          setShowCancelWarning(true);
                        }
                      }}
                      className={`w-full px-6 py-3 rounded-xl font-semibold transition-colors ${
                        membership.canceled
                          ? 'bg-emerald-200 hover:bg-emerald-300 text-emerald-900 border border-emerald-300' 
                          : 'bg-rose-100 hover:bg-rose-200 text-rose-900 border border-rose-200'
                      } ${msLoading ? 'opacity-60 cursor-not-allowed' : ''}`}
                    >
                      {msLoading ? 'Processing...' : (membership.canceled ? 'Resume Subscription' : 'Cancel Subscription')}
                    </button>

                    {/* Revised model: no base/pro plan switcher. */}
                    {hasPremiumAddon && !scheduledDowngrade && (
                      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-left space-y-2">
                        <h4 className="text-sm font-bold text-amber-950">Unlimited add-on</h4>
                        <p className="text-xs text-amber-900/90 leading-relaxed">
                          Schedule downgrade to remove the add-on at your next billing cycle.
                        </p>
                        <button
                          type="button"
                          disabled={paBusy}
                          onClick={runDowngradeAddonOnRenewal}
                          className="w-full py-2.5 rounded-xl font-semibold bg-amber-200 text-amber-900 border border-amber-300 hover:bg-amber-300 disabled:opacity-50"
                        >
                          {paBusy ? 'Processing…' : 'Downgrade on next billing cycle'}
                        </button>
                      </div>
                    )}
                    {/* Quick Actions */}
                    <div className="pt-4 border-t border-gray-200">
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          disabled={msLoading}
                          onClick={() => alert('Invoices portal coming soon')}
                          className="text-amber-600 hover:text-amber-700 text-sm font-medium py-2 px-3 rounded-lg hover:bg-amber-50 transition-colors"
                        >
                          View Invoices
                        </button>
                        <button
                          disabled={msLoading}
                          onClick={() => alert('Update card via Stripe portal coming soon')}
                          className="text-amber-600 hover:text-amber-700 text-sm font-medium py-2 px-3 rounded-lg hover:bg-amber-50 transition-colors"
                        >
                          Update Card
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              )}

              {/* Simple Plan Change Confirmation Modal */}
              {planConfirm && (
                <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4">
                  <div className="bg-white w-full max-w-4xl rounded-xl shadow-xl">
                    {/* Header */}
          <div className="bg-gradient-to-r from-[#FDF3D6] via-[#FBEEC8] to-[#F8E8BE] p-6 text-amber-950 border-b border-amber-100 shadow-sm">
                      <div className="flex items-center justify-between">
                        <div>
                          {(() => {
                            const isSamePlan = planConfirm.current === true;
                            const isTrialToPaid = isFreeTrial && !isSamePlan;

                            if (isTrialToPaid) {
                              return (
                                <>
                                  <h3 className="text-2xl font-bold">Upgrade to {planConfirm.label}</h3>
                                  <p className="text-amber-900/75 mt-1">
                                    Your free trial will end now and your {planConfirm.label.toLowerCase()} will start immediately.
                                  </p>
                                </>
                              );
                            }

                            if (!isSamePlan) {
                              return (
                                <>
                                  <h3 className="text-2xl font-bold">Switch to {planConfirm.label}</h3>
                                  <p className="text-amber-900/75 mt-1">
                                    Your new plan will start immediately after confirmation.
                                  </p>
                                </>
                              );
                            }

                            return (
                              <>
                                <h3 className="text-2xl font-bold">Current Plan: {planConfirm.label}</h3>
                                <p className="text-amber-900/75 mt-1">You're already on this plan</p>
                              </>
                            );
                          })()}
                        </div>
                        <button 
                          onClick={() => setPlanConfirm(null)}
                          className="text-amber-900/70 hover:text-amber-950 hover:bg-white/75 rounded-full p-2 transition-colors"
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div className="p-6">
                      {/* Compact Plan Comparison */}
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                        {/* Current Plan (read-only) */}
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <h4 className="text-sm font-bold text-gray-900 mb-3">Current Plan</h4>
                          <div className="flex items-center gap-2 mb-3">
                            <span className="px-3 py-1 bg-gray-700 text-white rounded-full text-xs font-bold">
                              {(membership.tier || 'base').toUpperCase()}
                            </span>
                          </div>
                          <div className="space-y-1 text-xs">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Status:</span>
                              <span className="font-bold text-green-600">{membership.status || 'active'}</span>
                            </div>
                          </div>
                        </div>

                        {/* New Plan (selected) */}
                        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-4 border border-amber-200">
                          <h4 className="text-sm font-bold text-amber-900 mb-3">New Plan</h4>
                          <div className="flex items-center gap-2 mb-3">
                            <span className="px-3 py-1 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-full text-xs font-bold">
                              {planConfirm.label.toUpperCase()}
                            </span>
                          </div>
                          <div className="space-y-1 text-xs">
                            <div className="flex justify-between">
                              <span className="text-amber-700">Rate:</span>
                              <span className="font-bold text-amber-900">
                                {planConfirm.price === 0 ? 'Free' : `₹${planConfirm.price}/month`}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Billing Summary */}
                        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-4 border border-amber-200">
                          <h4 className="text-sm font-bold text-amber-900 mb-3">Billing Summary</h4>
                          <div className="space-y-1 text-xs">
                            <div className="flex justify-between">
                              <span className="text-amber-700">Plan:</span>
                              <span className="font-bold text-amber-900">{planConfirm.label}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-amber-700">Monthly Rate:</span>
                              <span className="font-bold text-amber-900">
                                {planConfirm.price === 0 ? 'Free' : `₹${planConfirm.price}`}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-amber-700">Total Monthly:</span>
                              <span className="font-bold text-amber-900">
                                {planConfirm.price === 0 ? 'Free' : `₹${planConfirm.price.toLocaleString()}`}
                              </span>
                            </div>
                            <div className="flex justify-between bg-gray-100 rounded px-2 py-1 mt-2">
                              <span className="text-gray-800 font-bold">Status:</span>
                              <span className="font-bold text-gray-900">
                                {planConfirm.current
                                  ? 'Same plan – no changes'
                                  : isFreeTrial
                                  ? 'Upgrading from free trial'
                                  : 'Plan will update immediately'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Compact Features and Note */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                        {/* Plan Features */}
                        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-4 border border-amber-200">
                          <h4 className="text-sm font-bold text-amber-900 mb-3">New Plan Features</h4>
                          <div className="space-y-2">
                            {planConfirm.features.map((feature, index) => (
                              <div key={index} className="flex items-center gap-2">
                                <svg className="w-4 h-4 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span className="text-xs text-amber-800 font-medium">{feature}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Important Note */}
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                          <h4 className="text-sm font-bold text-blue-900 mb-3">Important Note</h4>
                          {planConfirm.current ? (
                            <p className="text-xs text-blue-800">
                              <strong>Same plan:</strong> You're already on this plan. No changes will be made to your account.
                            </p>
                          ) : isFreeTrial ? (
                            <p className="text-xs text-blue-800">
                              <strong>From free trial:</strong> Confirming will end your free trial today and start your paid {planConfirm.label.toLowerCase()} plan immediately.
                            </p>
                          ) : (
                            <p className="text-xs text-blue-800">
                              <strong>Plan change:</strong> Your new plan will start right away. Future renewals will use this plan and price.
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-3">
                        <button 
                          onClick={() => setPlanConfirm(null)} 
                          className="flex-1 px-4 py-3 border-2 border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50 transition-all duration-200 font-bold"
                        >
                          Cancel
                        </button>
                        <button
                          disabled={msLoading}
                          onClick={async () => {
                            if (!user) return;
                            setMsLoading(true); setMsError(""); setMsSuccess("");
                            try {
                              // Call the upgrade-plan API endpoint
                              const res = await authFetch(`${apiBase}/membership/upgrade-plan`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ 
                                  uid: user.uid, 
                                  newPlanType: planConfirm.id,
                                  seatCount: membership.seatsPurchased || membership.mentorSeatsPurchased || 1
                                })
                              });
                              
                              const data = await res.json();
                              if (!res.ok) throw new Error(data.error || 'Failed to change plan');
                              
                              // Update local state with new plan information
                              if (data.billing) {
                                setMsData(prev => ({
                                  ...prev,
                                  membership: {
                                    ...(prev?.membership || {}),
                                    tier: planConfirm.id,
                                    status: 'active',
                                    lastUpdated: new Date()
                                  }
                                }));
                              }
                              
                              setShowPlanPicker(false);
                              setPlanConfirm(null);
                              
                              // Show appropriate success message
                              const currentPlan = membership.tier || 'mentor-basic';
                              const newPlan = planConfirm.id;
                              const planOrder = { 'mentor-basic': 1, 'mentor-pro': 2, 'mentor-premium': 3 };
                              const isUpgrade = planOrder[newPlan] > planOrder[currentPlan];
                              const isDowngrade = planOrder[newPlan] < planOrder[currentPlan];
                              
                              if (isUpgrade) {
                                setMsSuccess(`Plan upgraded to ${planConfirm.label} successfully!`);
                              } else if (isDowngrade) {
                                setMsSuccess(`Plan will be downgraded to ${planConfirm.label} from next billing cycle!`);
                              } else {
                                setMsSuccess(`You're already on the ${planConfirm.label} plan!`);
                              }
                              
                              // Refresh the page after a short delay to show updated status
                              setTimeout(() => {
                                window.location.reload();
                              }, 2000);
                              
                            } catch (e) {
                              console.error('Plan change error:', e);
                              setMsError(e.message || 'Failed to change plan');
                            } finally {
                              setMsLoading(false);
                            }
                          }}
                          className={`flex-1 px-4 py-3 rounded-lg text-amber-950 border border-amber-300 font-bold transition-all duration-200 ${
                            msLoading ? 'bg-gray-200 text-gray-500 border-gray-300 cursor-not-allowed' : 'bg-amber-200 hover:bg-amber-300'
                          }`}
                        >
                          {msLoading ? 'Processing...' : (() => {
                            const currentPlan = membership.tier || 'mentor-basic';
                            const newPlan = planConfirm.id;
                            const planOrder = { 'mentor-basic': 1, 'mentor-pro': 2, 'mentor-premium': 3 };
                            const isUpgrade = planOrder[newPlan] > planOrder[currentPlan];
                            const isDowngrade = planOrder[newPlan] < planOrder[currentPlan];
                            
                            if (isUpgrade) {
                              return `Upgrade to ${planConfirm.label}`;
                            } else if (isDowngrade) {
                              return `Downgrade to ${planConfirm.label}`;
                            } else {
                              return `Current Plan: ${planConfirm.label}`;
                            }
                          })()}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* First paint with zero cache: skeleton only (no blocking copy). Cached users never see this. */}
              {!hasSubscriptionToShow && msRefreshing && <SubscriptionModalSkeleton />}


              {/* Footer Note */}
              <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Canceling your plan keeps access until the end of your current billing period.
                </p>
              </div>
            </div>
          </div>
        </div>

      {/* Cancellation Warning Modal */}
      {showCancelWarning && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[10000] p-4" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10000 }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 mx-auto my-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-red-600">⚠️ Cancel Subscription</h3>
              <button 
                onClick={() => setShowCancelWarning(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <ul className="text-sm text-red-700 space-y-1">
                  <li>• You will lose access to Artist Corner and premium activities</li>
                  <li>• Saved progress in premium spaces will be paused</li>
                  <li>• Any upcoming premium features will be unavailable</li>
                  <li>• Access will stop at the end of your current billing period</li>
                </ul>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  <strong>Good news:</strong> After your plan ends, you can renew your membership anytime from the 
                  <strong> Login page → Renew Membership</strong>.
                </p>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowCancelWarning(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Keep Subscription
                </button>
                <button
                  onClick={async () => {
                    setShowCancelWarning(false);
                    setMsLoading(true); 
                    setMsError("");
                    try {
                      const res = await authFetch(`${apiBase}/membership/portal`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ uid: user.uid, action: 'cancel' }),
                        signal: AbortSignal.timeout(15000), // 15s safety timeout so UI doesn't get stuck
                      });
                      const ct = res.headers.get('content-type') || '';
                      const j = ct.includes('application/json') ? await res.json() : null;
                      if (!res.ok) throw new Error((j && j.error) || 'Failed to cancel subscription');
                      const s = await authFetch(`${apiBase}/membership/status?uid=${user.uid}`);
                      const sct = s.headers.get('content-type') || '';
                      const sj = sct.includes('application/json') ? await s.json() : null;
                      if (!s.ok) throw new Error((sj && sj.error) || 'Failed to fetch status');
                      setMsData(sj);
                      setMsSuccess('Subscription canceled. After the plan expires, you can renew anytime through Login page → Renew Membership');
                      // Close the modal and trigger dashboard refresh
                      setTimeout(() => {
                        setShowCancelWarning(false);
                        onClose();
                        // Dispatch a custom event to notify the organization dashboard
                        window.dispatchEvent(new CustomEvent('subscriptionUpdated'));
                      }, 2000);
                    } catch (e) {
                      const msg = e?.name === 'AbortError'
                        ? 'Cancel request timed out. Please check your connection or try again in a moment.'
                        : (e.message || 'Failed to cancel subscription');
                      setMsError(msg);
                    } finally {
                      setMsLoading(false);
                    }
                  }}
                  className="flex-1 px-4 py-2 bg-rose-100 text-rose-900 border border-rose-200 rounded-lg hover:bg-rose-200"
                >
                  Yes, Cancel Subscription
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
